import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AddressPanel from 'components/AddressPanel/AddressPanel';
import Button from 'components/Button/Button';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DetailSection from 'components/DetailSection/DetailSection';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import ProfitCenterLinks from 'components/ProfitCenterLinks/ProfitCenterLinks';
import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import { NACE_CODE, NACE_SECTION, VALUE_CHAIN_LIST } from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useDataSetsDate from 'hooks/useDataSetsDate';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useGeoLocations from 'hooks/useGeoLocations';
import useScrollToError from 'hooks/useScrollToError';
import useValueChainPosition from 'hooks/useValueChainPosition';
import i18n from 'i18n';
import GeneralPanel from 'pages/UserPages/CreateSupplier/components/GeneralPanel';
import useCreateSupplierForm from 'pages/UserPages/CreateSupplier/hooks/useCreateSupplierForm';
import {
  useEnumQuery, useGetNaceCodeTreeQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums, selectNaceCodes } from 'store/slices/dataSets/selectors';
import { useSupplierMACreateMutation } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import getRequestDataForSupplier from 'utils/formHelpers/getRequestDataForSupplier';

import type { Path, PathValue } from 'react-hook-form';
import type ISupplierRecordRequest from 'store/slices/dataSets/interfaces/ISupplierRecordRequest';
import type IBaseQueryResponse from 'store/types/IBaseQueryResponse';
import type IErrorMessage from 'store/types/IErrorMessage';
import type { TCreateSupplierSchema } from 'utils/validators';
import type ICreateSupplierModal from './interfaces/ICreateSupplierModal';

import styles from './scss/CreateSupplierModal.module.scss';

function CreateSupplierModal({
  open, onClose, title,
}: ICreateSupplierModal) {
  const { t } = useTranslation();
  const { recordId = '', vcmId = 0 } = useParams();

  const placeholder = recordId ? t('common.notProvided') : '';
  const modalRef = useRef<HTMLDivElement>(null);

  const { data: enumData, isLoading: isEnumLoading } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.ownership, DATA_SETS_ENUM_TYPES.valueChainSection,
      DATA_SETS_ENUM_TYPES.businessUnitType, DATA_SETS_ENUM_TYPES.assetCategory, DATA_SETS_ENUM_TYPES.ownedLeased,
      DATA_SETS_ENUM_TYPES.upstreamTier],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  const { data: naceCodesData, isLoading: isNaceCodesTreeLoading } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });
  const [supplierCreate, { isLoading: isSupplierCreateLoading }] = useSupplierMACreateMutation();

  const { data: userData, isLoading: isUserMeLoading } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;

  const { setFieldsErrors } = useFieldsErrors();
  const isPageLoading = isEnumLoading || isNaceCodesTreeLoading || isUserMeLoading
  || isSupplierCreateLoading;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    fieldsName,
    watch,
    setValue,
    profitCenterIds,
    formState,
  } = useCreateSupplierForm({ isPageLoading });

  const {
    handleGetVcOptions,
    vcOptionsList,
    listPosition,
  } = useValueChainPosition(watch(VALUE_CHAIN_LIST)?.value);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const naceDivisions = watch(NACE_SECTION)?.divisions || [];

  const handleNaceSelectionChange = () => {
    const emptyOption = null as PathValue<TCreateSupplierSchema, Path<TCreateSupplierSchema>>;
    setValue(NACE_CODE, emptyOption);
  };

  const {
    dateOpened, onDateOpenedChange,
  } = useDataSetsDate<TCreateSupplierSchema>({ watch, setValue });

  const {
    onFindCountries, onFindStates, onFindCities, isStatesDisabled, onChangeCountry, onChangeState,
    isCitiesDisabled, defaultCountries,
  } = useGeoLocations<TCreateSupplierSchema>({ watch, setValue });

  useScrollToError({
    formState,
    scrollContainerRef: modalRef,
    scrollContainerSelector: '[class*="formWrapper"]',
  });

  const handleCreateSupplierSubmit = handleSubmit(async (values) => {
    const requestData: ISupplierRecordRequest = getRequestDataForSupplier({ values, companyCurrency });

    try {
      await supplierCreate({
        data: requestData,
        maId: recordId,
        vcmId: Number(vcmId),
      }).unwrap();

      toast.success(i18n.t(
        recordId ? 'notifications.recordHasBeenUpdated'
          : 'notifications.recordHasBeenAdded',
      ));
      reset(undefined, { keepValues: true });
      onClose();
    } catch (errors) {
      const fieldErrors = errors as IBaseQueryResponse<{ message: IErrorMessage<TCreateSupplierSchema>[][] }>;

      setFieldsErrors<TCreateSupplierSchema>({ errors: fieldErrors, setError, fieldsName });
    }
  });

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div ref={modalRef} className={styles.root}>
        <div className={styles.formWrapper}>
          <div className={styles.wrapper}>
            <Loader isPageLoading={isPageLoading} isFullLoader={false} />
            <GeneralPanel
              control={control}
              enumData={enumData}
              dateOpened={dateOpened}
              onDateOpenedChange={onDateOpenedChange}
              placeholder={placeholder}
              handleGetVcOptions={handleGetVcOptions}
              vcOptionsList={vcOptionsList}
              listPosition={listPosition}
              setValue={setValue}
            />
            <DetailSection
              control={control}
              naceCodesData={naceCodesData}
              naceDivisions={naceDivisions}
              onNaceSelectionChange={handleNaceSelectionChange}
              placeholder={placeholder}
              recordId={recordId}
            />
            <DataSetsPanel title={t('suppliers.selections.financials')} className={styles.fullWidth}>
              <ProfitCenterLinks
                control={control}
                profitCenterIds={profitCenterIds}
                placeholder={placeholder}
                watch={(field: string) => watch(field as never)}
                setValue={(field: string, value: unknown) => setValue(field as never, value as never)}
              />
            </DataSetsPanel>
            <AddressPanel
              control={control}
              placeholder={placeholder}
              onFindCountries={onFindCountries}
              onFindStates={onFindStates}
              onFindCities={onFindCities}
              isStatesDisabled={isStatesDisabled}
              isCitiesDisabled={isCitiesDisabled}
              onChangeCountry={onChangeCountry}
              onChangeState={onChangeState}
              menuPosition="absolute"
              defaultCountries={defaultCountries}
            />
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onClose}
          />
          <Button
            title={t('common.add')}
            onClick={handleCreateSupplierSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateSupplierModal;
